// ColorPicker.jsx - Custom color picker with click-and-drag support
import React, { useState, useRef, useEffect } from 'react';
import { Popover } from '@headlessui/react';
import chroma from 'chroma-js';

const ColorPicker = ({ color, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);

  const satLightRef = useRef(null);
  const hueRef = useRef(null);
  const [isDraggingSL, setIsDraggingSL] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);

  // Initialize HSL values from input color
  useEffect(() => {
    try {
      let chromaColor;
      if (typeof color === 'string') {
        chromaColor = chroma(color);
      } else if (Array.isArray(color) && color.length >= 3) {
        chromaColor = chroma.rgb(color[0], color[1], color[2]);
      } else {
        chromaColor = chroma('#FF0000');
      }

      const [h, s, l] = chromaColor.hsl();
      setHue(isNaN(h) ? 0 : h);
      setSaturation(isNaN(s) ? 0 : s * 100);
      setLightness(isNaN(l) ? 50 : l * 100);
    } catch (e) {
      console.error('Error parsing color:', e);
      setHue(0);
      setSaturation(100);
      setLightness(50);
    }
  }, [color, isOpen]);

  // Convert current HSL to color output
  const getCurrentColor = () => {
    try {
      const chromaColor = chroma.hsl(hue, saturation / 100, lightness / 100);
      const [r, g, b] = chromaColor.rgb();
      return [Math.round(r), Math.round(g), Math.round(b), 0.8];
    } catch (e) {
      return [255, 0, 0, 0.8];
    }
  };

  const colorToCss = (c) => {
    if (typeof c === 'string') return c;
    if (Array.isArray(c) && c.length >= 3) {
      const [r, g, b, a = 1] = c;
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
    }
    return '#FF0000';
  };

  // Handle saturation/lightness picker drag
  const handleSLMouseDown = (e) => {
    setIsDraggingSL(true);
    updateSLFromMouse(e);
  };

  const handleSLMouseMove = (e) => {
    if (isDraggingSL) {
      updateSLFromMouse(e);
    }
  };

  const handleSLMouseUp = () => {
    if (isDraggingSL) {
      setIsDraggingSL(false);
      // Don't apply color yet - user must click Apply button
    }
  };

  const updateSLFromMouse = (e) => {
    if (!satLightRef.current) return;
    const rect = satLightRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

    const newSat = (x / rect.width) * 100;
    const newLight = 100 - (y / rect.height) * 100;

    setSaturation(newSat);
    setLightness(newLight);
  };

  // Handle hue slider drag
  const handleHueMouseDown = (e) => {
    setIsDraggingHue(true);
    updateHueFromMouse(e);
  };

  const handleHueMouseMove = (e) => {
    if (isDraggingHue) {
      updateHueFromMouse(e);
    }
  };

  const handleHueMouseUp = () => {
    if (isDraggingHue) {
      setIsDraggingHue(false);
      // Don't apply color yet - user must click Apply button
    }
  };

  const updateHueFromMouse = (e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const newHue = (x / rect.width) * 360;
    setHue(newHue);
  };

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (isDraggingSL) {
      document.addEventListener('mousemove', handleSLMouseMove);
      document.addEventListener('mouseup', handleSLMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleSLMouseMove);
        document.removeEventListener('mouseup', handleSLMouseUp);
      };
    }
  }, [isDraggingSL, hue, saturation, lightness]);

  useEffect(() => {
    if (isDraggingHue) {
      document.addEventListener('mousemove', handleHueMouseMove);
      document.addEventListener('mouseup', handleHueMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleHueMouseMove);
        document.removeEventListener('mouseup', handleHueMouseUp);
      };
    }
  }, [isDraggingHue, saturation, lightness]);

  const currentColorCss = colorToCss(getCurrentColor());
  const displayColorCss = colorToCss(color);

  return (
    <Popover className="relative inline-block">
      {({ open, close }) => {
        // Sync internal state when popover opens/closes
        useEffect(() => {
          setIsOpen(open);
        }, [open]);

        return (
          <>
            <Popover.Button
              className={`w-8 h-8 rounded cursor-pointer border-2 border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`}
              style={{ backgroundColor: displayColorCss }}
            />

            {open && (
              <div className="fixed inset-0 z-40" onClick={close} />
            )}

            <Popover.Panel className="fixed z-50" style={{
              transform: 'translate(-50%, 10px)',
              left: '50%',
              top: '50%',
              marginTop: '-180px'
            }}>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-3">
                {/* Saturation/Lightness Picker */}
                <div
                  ref={satLightRef}
                  className="w-48 h-48 relative cursor-crosshair mb-3 rounded"
                  style={{
                    background: `
                      linear-gradient(to top, black, transparent),
                      linear-gradient(to right, white, hsl(${hue}, 100%, 50%))
                    `,
                  }}
                  onMouseDown={handleSLMouseDown}
                >
                  {/* Picker indicator */}
                  <div
                    className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md pointer-events-none"
                    style={{
                      left: `${saturation}%`,
                      top: `${100 - lightness}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: currentColorCss,
                    }}
                  />
                </div>

                {/* Hue Slider */}
                <div className="mb-3">
                  <div
                    ref={hueRef}
                    className="w-full h-4 relative cursor-pointer rounded"
                    style={{
                      background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                    }}
                    onMouseDown={handleHueMouseDown}
                  >
                    {/* Hue indicator */}
                    <div
                      className="absolute w-3 h-6 border-2 border-white rounded shadow-md pointer-events-none"
                      style={{
                        left: `${(hue / 360) * 100}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: `hsl(${hue}, 100%, 50%)`,
                      }}
                    />
                  </div>
                </div>

                {/* Color Preview */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Preview:
                  </div>
                  <div
                    className="w-16 h-8 rounded border border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: currentColorCss }}
                  />
                </div>

                {/* Apply Button */}
                <button
                  className="mt-3 w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onClick={() => {
                    onChange(getCurrentColor());
                    close();
                  }}
                >
                  Apply
                </button>
              </div>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};

export default ColorPicker;
